import { motion } from "framer-motion";
import { FC, useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { Button } from "@tennr/frontend/components/Button";
import { NavigationLink, type NavigationItemProps, type NavigationProps } from "@tennr/frontend/components/Navigation";
import { AnimatedLogo } from "@tennr/frontend/components/AnimatedLogo";
import { DynamicText } from "@tennr/frontend/components/DynamicText";
import { useBackgroundColor } from "@tennr/frontend/context";
import { checkLightBackground } from "@tennr/frontend/utils";
import "./DesktopNavigation.css";

const DesktopNavigationItem: FC<NavigationItemProps> = ({ item, active, className = "", onClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <>
      {item.subitems ? (
        <div
          className={`nav-submenu ${isMenuOpen ? "open" : ""}`}
          onMouseEnter={() => setIsMenuOpen(true)}
          onMouseLeave={() => setIsMenuOpen(false)}
        >
          <DynamicText
            as="button"
            className={`nav-item ${!isMenuOpen && item.subitems.some((item) => item.to === location.pathname) ? "active" : ""} ${className}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span>{item.title}</span>
            <svg width="11" height="7" viewBox="0 0 11 7" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.14994 1.49996L5.39258 5.74261L9.63522 1.49996" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </DynamicText>
          <ul>
            {item.subitems.map((subitem, i) => {
              return (
                <li key={i}>
                  <NavigationLink
                    item={subitem}
                    active={!!subitem.to && location.pathname === subitem.to}
                    className={className}
                    onClick={onClick}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <NavigationLink item={item} active={active} className={className} onClick={onClick} />
      )}
    </>
  );
};

export const DesktopNavigation: FC<NavigationProps> = ({ navigationItems, transparent, hideNearFooter }) => {
  const { currentColor } = useBackgroundColor();
  const isLightBackground = checkLightBackground(currentColor);
  const location = useLocation();

  return (
    <nav className={`navigation desktop-navigation ${transparent ? "transparent" : ""}`} style={{ transform: hideNearFooter ? 'translateY(-100%)' : undefined, transition: 'transform 0.5s ease' }}>
      <div className="desktop-navigation-content">
        <Link to="/" aria-label="Tennr" className="nav-logo-link">
          <motion.div className="nav-logo" transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}>
            <AnimatedLogo
              width={120}
              height={30}
              isLightBackground={isLightBackground}
              opacity={0.85}
              enableScrollEffects={false}
            />
          </motion.div>
        </Link>

        <motion.div className="nav-items" style={{ opacity: 100 }}>
          {navigationItems.map((item, i) => (
            <DesktopNavigationItem key={i} item={item} active={!!item.to && location.pathname === item.to} />
          ))}
        </motion.div>

        <motion.div>
          <Link to="/book" style={{ textDecoration: "none" }}>
            <Button key={"book-demo"} minWidth={160} minHeight={52} text={"BOOK A DEMO"} dontAnimateIntro={true} />
          </Link>
        </motion.div>
      </div>
    </nav>
  );
};
